import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { Edit, Trash2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { getStatusChoices, getDocumentEndpoint, getDocumentTypeName, formatStatusLabel, type DocumentType } from '@/utils/document';

interface StatusChangeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: string;
  documentType: DocumentType;
  documentId: number;
  documentNumber: string;
  onStatusChanged: () => void;
}

export function StatusChangeDialog({
  isOpen,
  onOpenChange,
  currentStatus,
  documentType,
  documentId,
  documentNumber,
  onStatusChanged
}: StatusChangeDialogProps) {
  const [newStatus, setNewStatus] = useState<string>(currentStatus);
  const [notes, setNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const typeName = getDocumentTypeName(documentType);
  const endpoint = getDocumentEndpoint(documentId);
  const statusChoices = getStatusChoices(documentType);

  const handleStatusUpdate = async () => {
    if (newStatus === currentStatus) {
      onOpenChange(false);
      return;
    }

    try {
      setIsUpdating(true);
      
      await api.patch(endpoint, {
        status: newStatus,
        status_change_notes: notes
      });

      toast({
        title: "Status Updated",
        description: `${typeName} ${documentNumber} status changed to ${newStatus}`,
      });

      onStatusChanged();
      onOpenChange(false);
      setNotes('');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Status</DialogTitle>
          <DialogDescription>
            Update the status of {typeName} {documentNumber}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Current Status</Label>
            <div className="p-2 bg-muted rounded text-sm font-medium">
              {formatStatusLabel(currentStatus)}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-status">New Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {statusChoices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note about this status change..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleStatusUpdate} 
            disabled={isUpdating || newStatus === currentStatus}
          >
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: DocumentType;
  documentId: number;
  documentNumber: string;
  onDeleted: () => void;
}

export function DeleteConfirmationDialog({
  isOpen,
  onOpenChange,
  documentType,
  documentId,
  documentNumber,
  onDeleted
}: DeleteConfirmationDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const typeName = getDocumentTypeName(documentType);
  const endpoint = getDocumentEndpoint(documentId);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      
      await api.delete(endpoint);

      toast({
        title: "Deleted",
        description: `${typeName} ${documentNumber} has been deleted`,
      });

      onDeleted();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete document",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete {typeName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {typeName} {documentNumber}? 
            This action cannot be undone and will permanently remove the document 
            and all associated data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface DocumentActionsProps {
  documentType: DocumentType;
  documentId: number;
  documentNumber: string;
  currentStatus: string;
  onStatusChanged: () => void;
  onDeleted: () => void;
  showDelete?: boolean;
  showStatusChange?: boolean;
}

export function DocumentActions({
  documentType,
  documentId,
  documentNumber,
  currentStatus,
  onStatusChanged,
  onDeleted,
  showDelete = true,
  showStatusChange = true
}: DocumentActionsProps) {
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <div className="flex gap-2">
      {showStatusChange && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsStatusDialogOpen(true)}
        >
          <Edit className="h-4 w-4 mr-1" />
          Change Status
        </Button>
      )}

      {showDelete && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsDeleteDialogOpen(true)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>
      )}

      <StatusChangeDialog
        isOpen={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        currentStatus={currentStatus}
        documentType={documentType}
        documentId={documentId}
        documentNumber={documentNumber}
        onStatusChanged={onStatusChanged}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        documentType={documentType}
        documentId={documentId}
        documentNumber={documentNumber}
        onDeleted={onDeleted}
      />
    </div>
  );
}
